import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';

import { MediaKindSchema } from '../media-kind/media-kind';

/**
 * What a client asks for before uploading (N12).
 *
 * It declares **what it intends to send** — kind, type and size — and nothing
 * about where it goes: the storage key, the bucket and the expiry are composed
 * by the server, which is what makes "a client never constructs a storage URL"
 * structural rather than a convention. `sizeBytes` is required because the
 * signature covers `Content-Length`: a client that lies is refused by storage
 * itself, before a byte is written.
 */
export const PresignUploadRequestSchema = z
  .object({
    kind: MediaKindSchema,
    mime: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
  })
  .strict();

export type PresignUploadRequest = z.infer<typeof PresignUploadRequestSchema>;

/**
 * The minted upload. The client PUTs the bytes to `url` with exactly `headers`
 * — the signature covers them, so an added or altered header is a rejected
 * upload — and then calls finalize.
 *
 * `expiresAt` is advisory to the client and authoritative in the signature; the
 * row it reserved is swept by the purge worker if the upload never happens.
 */
export const PresignedUploadSchema = z
  .object({
    mediaId: UuidSchema,
    url: z.url(),
    method: z.literal('PUT'),
    headers: z.record(z.string(), z.string()),
    expiresAt: z.iso.datetime(),
  })
  .strict();

export type PresignedUpload = z.infer<typeof PresignedUploadSchema>;
